import { useTranslation } from 'react-i18next';
import { ChartSeries, tableCellValue } from 'helpers/chartDataProcessors';
import styles from './TableChart.module.css';
import typoStyles from 'style-guide/TypographyGuide.module.css';

interface TableChartProps {
  chartData: ChartSeries[] | undefined;
  /** Draw a bar under each number, as the table-with-bars chart does. */
  bars?: boolean;
}

// Each series is a row; the first two plots are the serial number and name columns, so the values start at the third.
const FIRST_VALUE_COLUMN = 2;

interface CellProps {
  value: number | string | null;
  bar: boolean;
}

const Cell = ({ value, bar }: CellProps) => (
  <td className={typoStyles.body_two}>
    {bar ? (
      <div className="d-flex flex-row align-items-center gap-2">
        <div className="col-3">{value ?? '–'}</div>
        <progress className={styles.bar} value={typeof value === 'number' ? value : 0} max={100} title={`${value ?? '–'}`} />
      </div>
    ) : (
      value ?? '–'
    )}
  </td>
);

const EmptyChart = () => {
  const { t } = useTranslation();
  return (
    <>
      <div>
        <img className="empty-state-3" src="/icons/Empty_icon.svg" alt="empty visual" />
      </div>
      <div>
        <label className="label-style-4 mt-3">{t('EMPTY_STATE.NO_DATA_FOUND')}</label>
      </div>
      <div className="w-50">
        <label className="label-style-5 mt-3">Please add the data to view corresponding visualisation here</label>
      </div>
    </>
  );
};

export const TableChart = ({ chartData, bars = false }: TableChartProps) => {
  const rows = (chartData ?? []).filter(Boolean);
  if (rows.length === 0) return <EmptyChart />;

  return (
    <div className="table-responsive" id="tableChart">
      <table className="table table-hover metricTextColor">
        <thead>
          <tr>
            {rows[0].plots.map((plot, index) => (
              <th key={index} scope="col" className={typoStyles.sub_heading_two}>
                {plot.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <th scope="row" className={typoStyles.body_two}>
                {index + 1}
              </th>
              <td className={typoStyles.body_two}>{row.headerName}</td>
              {row.plots.slice(FIRST_VALUE_COLUMN).map((plot, column) => (
                <Cell key={column} value={tableCellValue(plot.value)} bar={bars} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
